import React from "react";

// Customizable Area Start
import {
  Box,
  Grid,
  Paper,
  styled,
  Button,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import { ArrowBack as ArrowBackIcon } from "@material-ui/icons";

import { colors } from "../../../blocks/utilities/src/Colors";
// Customizable Area End

import ReportSchedulerViewController, {
  Props,
} from "./ReportSchedulerViewController";

const Strings = {
  pageTitle: "Schedule Details",
  back: "Back",
  name: "Name",
  reportType: "Report Type",
  frequency: "Frequency",
  executionTime: "Execution Time",
  timezone: "Timezone",
  startDate: "Start Date",
  endDate: "End Date",
  emailSubject: "Email Subject",
  emailBody: "Email Body",
  status: "Status",
  customParams: "Custom Params",
  recipientsTo: "Recipients (To)",
  recipientsCc: "Recipients (CC)",
  lastRunAt: "Last Run At",
  nextRunAt: "Next Run At",
  createdBy: "Created By",
  createdAt: "Created At",
  notAvailable: "-",
};

export default class ReportSchedulerView extends ReportSchedulerViewController {
  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    // Customizable Area End
  }

  // Customizable Area Start
  renderDetailRow = (label: string, value: string | null | undefined) => {
    return (
      <Grid item xs={12} md={6}>
        <Box style={webStyle.detailRow}>
          <Typography style={webStyle.label}>{label}</Typography>
          <Typography style={webStyle.value}>
            {value || Strings.notAvailable}
          </Typography>
        </Box>
      </Grid>
    );
  };

  renderFullWidthRow = (label: string, value: string | null | undefined) => {
    return (
      <Grid item xs={12}>
        <Box style={webStyle.detailRow}>
          <Typography style={webStyle.label}>{label}</Typography>
          <Typography style={webStyle.valueMultiline}>
            {value || Strings.notAvailable}
          </Typography>
        </Box>
      </Grid>
    );
  };
  // Customizable Area End

  render() {
    // Customizable Area Start
    const { scheduleData, isLoading } = this.state;
    // Customizable Area End

    return (
      // Customizable Area Start
      <MainBoxWrapper>
        <Box className="pageWrapper">
          <div style={webStyle.headerWrapper}>
            <div style={webStyle.titleContainer}>
              <Button
                data-test-id="backButton"
                className="backButton"
                onClick={this.handleBack}
                startIcon={<ArrowBackIcon />}
              >
                {Strings.back}
              </Button>
              <PageTitle>{Strings.pageTitle}</PageTitle>
            </div>
          </div>

          {isLoading ? (
            <Box style={webStyle.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : scheduleData ? (
            <Paper style={webStyle.formContainer}>
              <Grid container spacing={3}>
                {this.renderDetailRow(Strings.name, scheduleData.name)}
                {this.renderDetailRow(
                  Strings.reportType,
                  scheduleData.report_type_label
                )}
                {this.renderDetailRow(Strings.frequency, scheduleData.rrule)}
                {this.renderDetailRow(
                  Strings.executionTime,
                  scheduleData.execution_time
                )}
                {this.renderDetailRow(Strings.timezone, scheduleData.timezone)}
                {this.renderDetailRow(Strings.status, scheduleData.status)}
                {this.renderDetailRow(Strings.startDate, scheduleData.start_date)}
                {this.renderDetailRow(Strings.endDate, scheduleData.end_date)}
                {this.renderDetailRow(Strings.lastRunAt, scheduleData.last_run_at)}
                {this.renderDetailRow(Strings.nextRunAt, scheduleData.next_run_at)}
                {this.renderFullWidthRow(
                  Strings.emailSubject,
                  scheduleData.email_subject
                )}
                {this.renderFullWidthRow(
                  Strings.emailBody,
                  scheduleData.email_body
                )}
                {this.renderFullWidthRow(
                  Strings.recipientsTo,
                  scheduleData.recipients?.to?.map((r: {id: number, email: string}) => r.email).join(", ")
                )}
                {this.renderFullWidthRow(
                  Strings.recipientsCc,
                  scheduleData.recipients?.cc?.map((r: {id: number, email: string}) => r.email).join(", ")
                )}
                {this.renderFullWidthRow(
                  Strings.customParams,
                  scheduleData.custom_params
                    ? JSON.stringify(scheduleData.custom_params, null, 2)
                    : null
                )}
                {this.renderDetailRow(Strings.createdBy, scheduleData.created_by)}
                {this.renderDetailRow(Strings.createdAt, scheduleData.created_at)}
              </Grid>
            </Paper>
          ) : (
            <Paper style={webStyle.formContainer}>
              <Typography style={webStyle.noData}>
                Schedule not found
              </Typography>
            </Paper>
          )}
        </Box>
      </MainBoxWrapper>
      // Customizable Area End
    );
  }
}

// Customizable Area Start
export const PageTitle = styled("div")({
  fontFamily: "Montserrat",
  fontStyle: "normal",
  fontWeight: 600,
  fontSize: "24px",
  lineHeight: "29px",
  "@media only screen and (max-width: 1024px)": {
    fontSize: "16px",
  },
});

const MainBoxWrapper = styled("div")({
  "& .backButton": {
    fontWeight: 600,
    fontSize: "14px",
    color: colors().cyancobaltblue,
    textTransform: "unset",
    marginRight: "16px",
    "@media only screen and (max-width: 1024px)": {
      fontSize: "12px",
    },
  },
});

export const webStyle = {
  headerWrapper: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 24,
    alignItems: "center",
    flexWrap: "wrap" as "wrap",
  },
  titleContainer: {
    display: "flex",
    alignItems: "center",
  },
  formContainer: {
    padding: "24px",
    borderRadius: "8px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
  detailRow: {
    marginBottom: "8px",
  },
  label: {
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontWeight: 500,
    color: "#666666",
    marginBottom: "4px",
  },
  value: {
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontWeight: 600,
    color: "#333333",
  },
  valueMultiline: {
    fontFamily: "Montserrat",
    fontSize: "14px",
    fontWeight: 600,
    color: "#333333",
    whiteSpace: "pre-wrap" as "pre-wrap",
    wordBreak: "break-word" as "break-word",
  },
  noData: {
    fontFamily: "Montserrat",
    fontSize: "16px",
    fontWeight: 500,
    textAlign: "center" as "center",
    padding: "40px",
    color: "#666666",
  },
};
// Customizable Area End
